import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import apiRequest from '@utils/apiRequest'
import Emitter from '@utils/eventEmitter'
import { onDownloadFile } from '@utils/download'
import { toStringByFormatting } from '@utils/dateUtils'

export const adminGetMemberList = createAsyncThunk(
  'ADMIN_GET_MEMBER_LIST',
  async (params) => {
    let options = {
      method: 'GET',
      url: `${process.env.REACT_APP_AP_ADMIN_SERVER_HOST}/member`,
      params
    }
    let response = await apiRequest(options)
    if (response.__isError) { return }
    return {
      data: response,
    }
  }
)

export const adminGetMemberHistoryList = createAsyncThunk(
  'ADMIN_GET_MEMBER_HISTORY_LIST',
  async (params) => {
    let options = {
      method: 'GET',
      url: `${process.env.REACT_APP_AP_ADMIN_SERVER_HOST}/member/history`,
      params
    }
    let response = await apiRequest(options)
    if (response.__isError) { return }
    return {
      data: response,
    }
  }
)

export const adminGetMemberWithExcel = createAsyncThunk(
  'ADMIN_GET_MEMBER_WITH_EXCEL',
  async (data) => {
    let options = {
      method: 'POST',
      url: `${process.env.REACT_APP_AP_ADMIN_SERVER_HOST}/member/excel`,
      data,
      responseType: 'blob',
    }
    let response = await apiRequest(options)
    if (response.__isError) { return }
    return {
      data: response,
      fileName: data.groupName,
    }
  }
)

export const adminCheckMember = createAsyncThunk(
  'ADMIN_CHECK_MEMBER',
  async (data) => {
    let response = await Promise.all(data.checkList.map(check => (
      apiRequest({
        method: 'POST',
        url: `${process.env.REACT_APP_AP_ADMIN_SERVER_HOST}/member/check/${check.checkId}`,
      })
    )))
    if (response.__isError) { return }
    return true
  }
)

export const adminAddMember = createAsyncThunk(
  'ADMIN_ADD_MEMBER',
  async (data) => {
    let options = {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      method: 'POST',
      url: `${process.env.REACT_APP_AP_ADMIN_SERVER_HOST}/member`,
      data: data.form
    }
    let response = await apiRequest(options)
    if (response.__isError) {
      return
    }
    return {
      data: response,
    }
  }
)

export const adminUpdateMember = createAsyncThunk(
  'ADMIN_UPDATE_MEMBER',
  async (data) => {
    let options = {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      method: 'PUT',
      url: `${process.env.REACT_APP_AP_ADMIN_SERVER_HOST}/member/${data.companyMemberCode}`,
      data: data.form
    }
    let response = await apiRequest(options)
    if (response.__isError) {
      return
    }
    return {
      data: response,
    }
  }
)

export const adminDeleteMember = createAsyncThunk(
  'ADMIN_DELETE_COMPANY',
  async (data) => {
    let options = {
      method: 'DELETE',
      url: `${process.env.REACT_APP_AP_ADMIN_SERVER_HOST}/member/${data.companyMemberCode}`,
    }
    let response = await apiRequest(options)
    if (response.__isError) { return }
    return {
      data
    }
  }
)

export const adminDeleteSelectedMember = createAsyncThunk(
  'ADMIN_DELETE_SELECTED_MEMBER',
  async (data) => {
    let response = await Promise.all(data.selecteds.map(companyMemberCode => (
      apiRequest({
        method: 'DELETE',
        url: `${process.env.REACT_APP_AP_ADMIN_SERVER_HOST}/member/${companyMemberCode}`,
      })
    )))
    if (response.__isError) { return }
    return {
      data
    }
  }
)

export const adminDownloadMemberAttach = createAsyncThunk(
  'ADMIN_DOWNLOAD_MEMBER_ATTACH',
  async (params) => {
    let options = {
      method: 'GET',
      url: `${process.env.REACT_APP_AP_ADMIN_SERVER_HOST}/member/attach`,
      params,
      responseType: 'blob',
    }
    let response = await apiRequest(options)
    if (response.__isError) {
      return
    }
    return {
      fileName: params.fileName,
      data: response
    }
  }
)


const adminMemberSlice = createSlice({
  name: 'adminMember',
  initialState: {
    isLoading: false,
    adminMemberList: [],
    adminMemberListTotalSize: 0,
    adminMemberHistoryList: [],
  },
  reducers: {
    clearAdminMemberList: (state) => {
      state.adminMemberList = []
      state.adminMemberListTotalSize = 0
    },
    clearAdminMemberHistoryList: (state) => {
      state.adminMemberHistoryList = []
    },
  },
  extraReducers: {
    [adminGetMemberList.pending]: (state) => {
      state.isLoading = true
    },
    [adminGetMemberList.rejected]: (state) => {
      state.isLoading = false
    },
    [adminGetMemberList.fulfilled]: (state, { payload }) => {
      state.isLoading = false
      state.adminMemberListTotalSize = payload.data.totalElements
      state.adminMemberList = payload.data.content
    },
    [adminGetMemberHistoryList.fulfilled]: (state, { payload }) => {
      state.adminMemberHistoryList = payload.data
    },
    [adminUpdateMember.fulfilled]: (state, { payload }) => {
      if (!payload) {
        return
      }

      Emitter.emit('SHOW_MESSAGE', {
        text: `변경되었습니다.`
      })

      state.adminMemberList = state.adminMemberList.map(member => {
        if (member.companyMemberCode === payload.data.companyMemberCode) {
          let tempResignExpectDate = payload.data.resignExpectDate
          let tempBirth = payload.data.birth

          if (typeof payload.data.resignExpectDate !== "string") {
            tempResignExpectDate = toStringByFormatting(payload.data.resignExpectDate)
          }

          if (typeof payload.data.birth !== "string") {
            tempBirth = toStringByFormatting(payload.data.birth)
          }

          return {
            ...payload.data,
            birth: tempBirth,
            signExpectDate: payload.data.signExpectDate,
            resignExpectDate: tempResignExpectDate
          }
        } else {
          return member
        }
      })
    },
    [adminCheckMember.fulfilled]: (state, { payload }) => {
      if (!payload.data) {
        return
      }

      Emitter.emit('SHOW_MESSAGE', {
        text: `확인되었습니다.`
      })
    },
    [adminAddMember.fulfilled] : (state, { payload }) => {
      if (!payload) {
        return
      }

      Emitter.emit('SHOW_MESSAGE', {
        text: `등록되었습니다.`
      })
    },
    [adminUpdateMember.fulfilled]: (state, { payload }) => {
      if (!payload) {
        return
      }

      Emitter.emit('SHOW_MESSAGE', {
        text: `변경되었습니다.`
      })

      state.adminMemberList = state.adminMemberList.map(member => {
        if (member.companyMemberCode === payload.data.companyMemberCode) {
          return payload.data
        } else {
          return member
        }
      })
    },
    [adminDeleteMember.fulfilled]: (state, { payload }) => {
      if (!payload) {
        return
      }

      Emitter.emit('HIDE_CONFIRM')
      Emitter.emit('SHOW_MESSAGE', {
        text: '삭제되었습니다.'
      })

      state.adminMemberList = state.adminMemberList.filter(member => member.companyMemberCode !== payload.data.companyMemberCode)
    },
    [adminDeleteSelectedMember.fulfilled]: (state, { payload }) => {
      if (!payload) {
        return
      }

      Emitter.emit('HIDE_CONFIRM')
      Emitter.emit('SHOW_MESSAGE', {
        text: `${payload.data.selecteds.length}개 삭제되었습니다.`
      })

      state.adminMemberList = state.adminMemberList.filter(member => !payload.data.selecteds.includes(member.companyMemberCode))
    },
    [adminGetMemberWithExcel.fulfilled]: (state, { payload }) => {
      if (!payload) {
        return
      }

      const url = window.URL.createObjectURL(new Blob([payload.data]))
      const fileName = `member_excel[${payload.fileName}].xlsx`

      onDownloadFile({url, fileName})
    },
    [adminDownloadMemberAttach.fulfilled]: (state, { payload }) => {
      if (!payload) {
        return
      }

      const url = window.URL.createObjectURL(new Blob([payload.data]))
      const fileName = payload.fileName

      onDownloadFile({url, fileName})
    },
  }
})

export const { clearAdminMemberList, clearAdminMemberHistoryList } = adminMemberSlice.actions

export default adminMemberSlice.reducer